import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function FAQBot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    http.get("/faq").then((r) => {
      setItems(r.data.items || []);
      setMessages([
        {
          role: "bot",
          text: `Hi ${user.name?.split(" ")[0] || "there"}! I'm the KOI helper. Ask me anything about your ${roleLabel(user.role)} dashboard — or tap a quick question below.`,
        },
      ]);
    }).catch(() => { /* noop */ });
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const askQuick = (item) => {
    setMessages((m) => [...m, { role: "user", text: item.q }, { role: "bot", text: item.a }]);
  };

  const send = async () => {
    const query = input.trim();
    if (!query || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: query }]);
    try {
      const { data } = await http.post("/faq/ask", { query });
      if (data.match) {
        setMessages((m) => [...m, { role: "bot", text: data.match.a }]);
      } else {
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text: "I couldn't find an exact match. Try one of these related questions:",
            suggestions: data.suggestions || [],
          },
        ]);
      }
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Sorry, I couldn't reach the KOI helper right now." }]);
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="faq-bot-toggle"
        aria-label={open ? "Close KOI helper" : "Open KOI helper"}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-xl grid place-items-center transition-transform hover:scale-105"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="faq-bot-panel"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 max-h-[70vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="px-4 py-3 bg-gradient-to-r from-sky-500 to-indigo-600 text-white">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <div className="font-heading font-bold">KOI Helper</div>
                <div className="ml-auto text-[10px] uppercase tracking-widest opacity-85">{roleLabel(user.role)}</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`faq-msg-${m.role}-${i}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-sky-500 text-white rounded-br-sm"
                        : "bg-slate-100 text-slate-800 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                    {m.suggestions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.suggestions.map((s, j) => (
                          <button
                            key={j}
                            data-testid={`faq-suggestion-${i}-${j}`}
                            onClick={() => askQuick(s)}
                            className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-sky-50 hover:border-sky-300 transition-colors"
                          >
                            {s.q}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {messages.length === 1 && items.length > 0 && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Quick questions</div>
                  <div className="flex flex-wrap gap-1.5">
                    {items.slice(0, 6).map((it, i) => (
                      <button
                        key={i}
                        data-testid={`faq-quick-${i}`}
                        onClick={() => askQuick(it)}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-800 hover:bg-sky-100 transition-colors"
                      >
                        {it.q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  data-testid="faq-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask a question…"
                  className="flex-1 h-9"
                />
                <Button data-testid="faq-send-btn" size="icon" onClick={send} disabled={sending || !input.trim()} className="bg-sky-500 hover:bg-sky-600">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-2 text-[10px] text-slate-400 text-center">
                Answers are curated for your <span className="font-semibold text-slate-600">{roleLabel(user.role)}</span> role.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function roleLabel(r) {
  return ({ parent: "Parent", teacher: "Teacher", school_admin: "School Admin", admin: "Super Admin" }[r]) || r;
}
